import React, { useEffect, useState } from 'react';
import { Copy, Check, ArrowUpRight } from 'lucide-react';
import { fetchCreatorDashboard } from '../../lib/api';
import StatsCard from './StatsCard';
import PostsTable from './PostsTable';
import GlobalLeaderboard from './GlobalLeaderboard';

/**
 * Self-contained Creator Dashboard. Receives a creator handle and renders the
 * full overview: identity card, stat tiles, posts performance table, recent
 * orders ledger, and the global leaderboard with the active creator highlighted.
 */
const Dashboard = ({ handle }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!handle) return;
    setLoading(true);
    setError('');
    fetchCreatorDashboard(handle)
      .then(setData)
      .catch((err) => {
        const detail = err?.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : 'Could not load dashboard.');
      })
      .finally(() => setLoading(false));
  }, [handle]);

  if (!handle) {
    return null;
  }

  if (loading) {
    return (
      <div data-testid="dashboard-loading" className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-40 bg-[#C1D7E2]/40 animate-pulse" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div
        data-testid="dashboard-error"
        className="border border-[#C1D7E2] bg-[#F4F8F9] p-10 text-center"
      >
        <p className="font-serif italic text-2xl text-[#7C9CB1]">{error}</p>
        <p className="mt-3 font-mono text-[11px] tracking-[0.2em] uppercase text-[#90B0BF]">
          Check the handle and try again.
        </p>
      </div>
    );
  }

  const { creator, totals, posts, recent_orders: recentOrders } = data;

  const copyRef = () => {
    navigator.clipboard?.writeText(creator.ref_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div data-testid="dashboard-view" className="space-y-16">
      {/* Identity row */}
      <header
        data-testid="dashboard-identity"
        className="flex flex-col md:flex-row items-start md:items-center gap-6 pb-8 border-b border-[#C1D7E2]"
      >
        {creator.avatar_url ? (
          <img
            src={creator.avatar_url}
            alt={creator.display_name}
            className="w-20 h-20 rounded-full object-cover border border-[#C1D7E2]"
          />
        ) : (
          <div className="w-20 h-20 rounded-full bg-[#C1D7E2]" />
        )}
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF]">
            Creator dashboard
          </p>
          <h2 className="font-serif text-4xl md:text-5xl text-[#7C9CB1] font-light leading-tight">
            {creator.display_name}
          </h2>
          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 font-mono text-[11px] text-[#90B0BF]">
            <span>@{creator.handle}</span>
            <span className="uppercase tracking-[0.25em]">Status · {creator.status}</span>
            {creator.instagram && <span>IG {creator.instagram}</span>}
            {creator.tiktok && <span>TT {creator.tiktok}</span>}
          </div>
        </div>
        <button
          onClick={copyRef}
          data-testid="dashboard-copy-ref"
          className="inline-flex items-center gap-2 border border-[#7C9CB1] text-[#7C9CB1] px-5 py-3 font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-[#C1D7E2]/40 transition-colors"
        >
          {copied ? <Check size={14} strokeWidth={1.5} /> : <Copy size={14} strokeWidth={1.5} />}
          {creator.ref_code}
        </button>
      </header>

      {/* Stat tiles */}
      <section data-testid="dashboard-stats" className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <StatsCard
          testId="stat-seva"
          accent
          label="$SEVA earned"
          value={Number(totals.seva_earned || 0).toFixed(2)}
          sub={`From ${totals.sales_count} attributed sales`}
        />
        <StatsCard
          testId="stat-volume"
          label="Sales volume"
          value={`$${Number(totals.sales_volume_usd || 0).toFixed(0)}`}
          sub="Lifetime, USD"
        />
        <StatsCard
          testId="stat-views"
          label="Views"
          value={totals.views.toLocaleString()}
          sub={`${totals.shares} shares`}
        />
        <StatsCard
          testId="stat-clicks"
          label="Clicks"
          value={totals.clicks.toLocaleString()}
          sub={`${totals.click_through_rate}% CTR · ${totals.conversion_rate}% CVR`}
        />
      </section>

      {/* Posts table */}
      <section data-testid="dashboard-posts">
        <div className="flex items-baseline justify-between flex-wrap gap-3 mb-6">
          <h3 className="font-serif text-3xl text-[#7C9CB1] font-light">
            Your content
          </h3>
          <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#90B0BF]">
            {totals.posts_count} posts
          </p>
        </div>
        <PostsTable posts={posts} />
      </section>

      {/* Two-column: recent orders + global leaderboard */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-7" data-testid="dashboard-recent-orders">
          <h3 className="font-serif text-3xl text-[#7C9CB1] font-light mb-6">
            Recent attributed orders
          </h3>
          {recentOrders.length === 0 ? (
            <p className="font-serif italic text-[#90B0BF] py-6">
              No attributed orders yet. Share a post to start the chain.
            </p>
          ) : (
            <ol className="divide-y divide-[#C1D7E2]">
              {recentOrders.map((o, i) => (
                <li
                  key={o.id}
                  data-testid={`dashboard-order-${i}`}
                  className="py-4 flex items-baseline justify-between gap-4 flex-wrap"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-serif text-lg text-[#7C9CB1] truncate">
                      {o.items[0]?.name}
                      {o.items.length > 1 && (
                        <span className="font-mono text-[11px] text-[#90B0BF]">
                          {' '}+{o.items.length - 1} more
                        </span>
                      )}
                    </p>
                    <p className="font-mono text-[11px] text-[#90B0BF] mt-1">
                      {new Date(o.created_at).toLocaleString()} · ref {o.ref_used || '—'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-sm text-[#7C9CB1]">
                      ${o.subtotal_usd.toFixed(2)}
                    </p>
                    <p className="font-mono text-[11px] text-[#90B0BF]">
                      +{o.creator_seva.toFixed(2)} SEVA
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>

        <aside className="lg:col-span-5" data-testid="dashboard-global-leaderboard">
          <div className="flex items-baseline justify-between mb-6">
            <h3 className="font-serif text-3xl text-[#7C9CB1] font-light">
              Global leaderboard
            </h3>
            <a
              href="/sevagram"
              className="inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.25em] uppercase text-[#7C9CB1] hover:text-[#90B0BF]"
            >
              Feed <ArrowUpRight size={11} strokeWidth={1.5} />
            </a>
          </div>
          <GlobalLeaderboard activeHandle={creator.handle} />
        </aside>
      </section>
    </div>
  );
};

export default Dashboard;
